import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * When employer/subcontractor dashboard is mounted, exposes sidebar items for the main Header
 * (desktop: horizontal nav; mobile slide-out: same items when on shell routes).
 */
const DashboardNavContext = createContext(null);

/** @typedef {{ id: string, label: string, active: boolean, badge?: number, onActivate: () => void }} DashboardNavItem */

export function DashboardNavProvider({ children }) {
  const [navItems, setNavItemsState] = useState(null);

  const setDashboardNav = useCallback((/** @type {DashboardNavItem[] | null} */ items) => {
    setNavItemsState(items && items.length > 0 ? items : null);
  }, []);

  const clearDashboardNav = useCallback(() => {
    setNavItemsState(null);
  }, []);

  const value = useMemo(
    () => ({ navItems, setDashboardNav, clearDashboardNav }),
    [navItems, setDashboardNav, clearDashboardNav]
  );

  return <DashboardNavContext.Provider value={value}>{children}</DashboardNavContext.Provider>;
}

export function useDashboardNav() {
  const ctx = useContext(DashboardNavContext);
  if (!ctx) {
    throw new Error('useDashboardNav must be used within DashboardNavProvider');
  }
  return ctx;
}

/**
 * Authenticated app / dashboard routes — hide global marketing footer, etc.
 * Keep in sync with UnifiedRoutes paths in App.js.
 */
const APP_DASHBOARD_PATHS = new Set([
  '/dashboard',
  '/dashboard/employer',
  '/dashboard/subcontractor',
  '/subcontractors',
  '/reports',
  '/settings',
  '/application',
  '/applications',
  '/profile',
  '/profile/organization',
  '/profile/users',
]);

export function isAppDashboardRoute(pathname) {
  if (!pathname) return false;
  const p = pathname.replace(/\/$/, '') || '/';
  if (APP_DASHBOARD_PATHS.has(p)) return true;
  if (p.startsWith('/profile')) return true;
  return false;
}

/** Login / registration — no global header or footer (pages are full-bleed). */
const LOGIN_SIGNUP_PATHS = new Set(['/login', '/register', '/register-subcontractor']);

export function isLoginOrSignupRoute(pathname) {
  if (!pathname) return false;
  const p = pathname.replace(/\/$/, '') || '/';
  return LOGIN_SIGNUP_PATHS.has(p);
}

/** Routes that render CompanyDashboard or SubcontractorDashboard (shell with sidebar). */
export function isEmployerSubcontractorShellPath(pathname) {
  if (!pathname) return false;
  const p = pathname.replace(/\/$/, '') || '/';
  if (p === '/dashboard' || p === '/dashboard/employer' || p === '/dashboard/subcontractor') return true;
  if (p === '/profile' || p === '/profile/organization' || p === '/profile/users') return true;
  return ['/subcontractors', '/reports', '/settings', '/application'].includes(p);
}
